import React, { useState, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Archive, Box, Mail, RotateCcw, Zap, } from "feather-icons-react/build/IconComponents";
import Select from "react-select";
import { Filter } from "react-feather";
import EditProduct from "../../core/modals/inventory/EditProduct";
import useStockData from "../../hooks/useStockData";
import Table from "../../core/pagination/datatable";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { deleteProduct } from "../../api/inventoryApi";
import { removeProduct } from "../../slices/productSlice";
import { useDispatch } from "react-redux";

const LowStock = () => {
  const { outOfProducts, lowProducts, products, categoryData } = useStockData();
  const dispatch = useDispatch();

  // États pour l'affichage des filtres et la recherche
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // États pour les filtres via react-select (filtre)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  
  // États pour l'édition
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedEditProduct, setSelectedEditProduct] = useState(null);

  // Fonction pour afficher/masquer les filtres
  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  // Options pour les filtres
  const productlist = products.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const category = categoryData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const store = [
    { value: "Bar", label: "Bar" },
    { value: "Exposition", label: "Exposition" },
    { value: "Cuisine", label: "Cuisine" },
    { value: "Stock général", label: "Stock général" },
  ];

  // Tooltips pour les boutons PDF, Excel et Refresh
  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  // Fonction de filtrage des données
  const filterData = (data) => {
    return data.filter((item) => {
      const matchesSearch = searchTerm
        ? item.name.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      const matchesProduct = selectedProduct ? item.id === selectedProduct.value : true;
      const matchesCategory = selectedCategory
        ? item.category && item.category.id === selectedCategory.value
        : true;
      const matchesStore = selectedStore ? item.store === selectedStore.value : true;
      return matchesSearch && matchesProduct && matchesCategory && matchesStore;
    });
  };

  // Mémorisation des données filtrées pour chaque onglet
  const filteredLowProducts = useMemo(
    () => filterData(lowProducts),
    [lowProducts, searchTerm, selectedProduct, selectedCategory, selectedStore]
  );
  const filteredOutOfProducts = useMemo(
    () => filterData(outOfProducts),
    [outOfProducts, searchTerm, selectedProduct, selectedCategory, selectedStore]
  );

  // Définition des colonnes pour le tableau, y compris la colonne "Action"
  const columns = [
    {
      title: "Créé par",
      dataIndex: "user",
      render: (user) => user?.name || "Non défini",
    },
    {
      title: "Stock",
      dataIndex: "stock",
      key: "stock",
    },
    {
      title: "Produit",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Catégorie",
      dataIndex: "category",
      render: (category) => category?.name || "Non défini",
    },
    {
      title: "Variantes",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.type}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Quantité",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.quantite}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Prix de vente",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.prix_vente}</strong> XOF
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Prix d'achat",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.prix_achat}</strong> XOF
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Qte alerte",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.alerte_quantite}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <button
              className="btn btn-sm btn-primary me-2"
              onClick={() => handleEdit(record)}
            >
              <i data-feather="edit" className="feather-edit"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(record.id)}
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </button>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.createdby?.length || 0) - (b.createdby?.length || 0),
    },
  ];

  // Fonctions d'édition et de suppression

  const handleEdit = (product) => {
    setSelectedEditProduct(product);
    setIsEditModalVisible(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr?",
      text: "Cette action est irréversible!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Oui, supprimer!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(removeProduct(id));
        deleteFunction(id);
      }
    });
  };

  const deleteFunction = async (productId) => {
    try {
      const response = await deleteProduct(productId);
      if (response?.error) {
        Swal.fire("Erreur", response.error.message || "Une erreur est survenue.", "error");
      } else {
        Swal.fire("Supprimé !", "Votre produit a été supprimé avec succès.", "success");
      }
    } catch (error) {
      Swal.fire(
        "Erreur",
        error?.response?.data?.message || "Une erreur imprévue est survenue.",
        "error"
      );
    }
  };

  // Fonctions pour télécharger en PDF et Excel (inchangées)
  const handleDownloadPDF = (data, title) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    doc.setFontSize(18);
    doc.text("Fiche d'expression de besoin", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(11);
    doc.text("LibrairieCafe", margin, 30);
    doc.text("Abomey-Calavi/Godomey", margin, 36);
    doc.text("Téléphone: ................................", margin, 42);
    doc.text("Email: contact@complexeelite.com", margin, 48);
    const today = new Date();
    const formattedDate = today.toLocaleDateString("fr-FR");
    doc.text(`Date: ${formattedDate}`, pageWidth - margin, 30, { align: "right" });
    doc.text(`Bon n°: ................`, pageWidth - margin, 36, { align: "right" });
    doc.line(margin, 52, pageWidth - margin, 52);
    const tableColumn = ["Produit","Quantité", "Prix Achat (XOF)", "Prix Vente (XOF)"];
    const tableRows = [];
    data.forEach((item) => {
      const productName = item.name;
      const quantites = ' ';
      const prixAchat =
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_achat).join(" / ")
          : "N/A";
      const prixVente =
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_vente).join(" / ")
          : "N/A";
      tableRows.push([productName, quantites, prixAchat, prixVente]);
    });
    const startY = 60;
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: startY,
      margin: { left: margin, right: margin },
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
      didDrawPage: () => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.text(`Page ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: "right" });
      },
    });
    doc.save(`${title}.pdf`);
  };

  const handleDownloadExcel = (data, title) => {
    const exportData = data.map((item) => ({
      "Produit": item.name,
      "Catégorie": item.category?.name || "Non défini",
      "Quantité":
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.quantite).join(", ")
          : "N/A",
      "Prix Achat (XOF)":
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_achat).join(", ")
          : "N/A",
      "Prix Vente (XOF)":
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_vente).join(", ")
          : "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${title}.xlsx`);
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title me-auto">
              <h4>Stocks faibles</h4>
              <h6>Gérez vos faibles stocks</h6>
            </div>
            <ul className="table-top-head">
              <li>
                <div className="status-toggle d-flex justify-content-between align-items-center">
                  <input type="checkbox" id="user2" className="check" defaultChecked={true} />
                  <label htmlFor="user2" className="checktoggle">
                    checkbox
                  </label>
                  Notifier
                </div>
              </li>
              <li>
                <Link to="" className="btn btn-secondary" data-bs-toggle="modal" data-bs-target="#send-email">
                  <Mail className="feather-mail" />
                  Envoyer un e-mail
                </Link>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link onClick={() => window.location.reload()}>
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>
          <div className="table-tab">
            <ul className="nav nav-pills" id="pills-tab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  id="pills-home-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-home"
                  type="button"
                  role="tab"
                  aria-controls="pills-home"
                  aria-selected="true"
                >
                  Stocks faibles
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link"
                  id="pills-profile-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-profile"
                  type="button"
                  role="tab"
                  aria-controls="pills-profile"
                  aria-selected="false"
                >
                  En rupture de stock
                </button>
              </li>
            </ul>
            <div className="tab-content" id="pills-tabContent">
              {/* Onglet Stocks faibles */}
              <div className="tab-pane fade show active" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab">
                <div className="card table-list-card">
                  <div className="card-body">
                    <div className="table-top">
                      <div className="search-set">
                        <div className="search-input">
                          <input
                            type="text"
                            placeholder="Search"
                            className="form-control form-control-sm formsearch"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <Link to="#" className="btn btn-searchset">
                            <i data-feather="search" className="feather-search" />
                          </Link>
                        </div>
                      </div>
                      {/* Bouton PDF pour les stocks faibles */}
                      <li>
                        <OverlayTrigger placement="top" overlay={renderTooltip}>
                          <Link onClick={() => handleDownloadPDF(filteredLowProducts, "Produits en stocks faibles")}>
                            <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                          </Link>
                        </OverlayTrigger>
                      </li>
                      {/* Bouton Excel pour les stocks faibles */}
                      <li>
                        <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                          <Link onClick={() => handleDownloadExcel(filteredLowProducts, "Produits en stocks faibles")}>
                            <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                          </Link>
                        </OverlayTrigger>
                      </li>
                      <div className="search-path">
                        <Link className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} id="filter_search">
                          <Filter className="filter-icon" onClick={toggleFilterVisibility} />
                          <span onClick={toggleFilterVisibility}>
                            <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                          </span>
                        </Link>
                      </div>
                    </div>
                    {/* Filtres */}
                    <div className={`card${isFilterVisible ? " visible" : ""}`} id="filter_inputs" style={{ display: isFilterVisible ? "block" : "none" }}>
                      <div className="card-body pb-0">
                        <div className="row">
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Box className="info-img" />
                              <Select
                                className="img-select"
                                options={productlist}
                                classNamePrefix="react-select"
                                placeholder="Choisis Produit"
                                value={selectedProduct}
                                onChange={setSelectedProduct}
                              />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Zap className="info-img" />
                              <Select
                                className="img-select"
                                options={category}
                                classNamePrefix="react-select"
                                placeholder="Choisis Catégorie"
                                value={selectedCategory}
                                onChange={setSelectedCategory}
                              />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Archive className="info-img" />
                              <Select
                                className="img-select"
                                options={store}
                                classNamePrefix="react-select"
                                placeholder="Choisis Warehouse"
                                value={selectedStore}
                                onChange={setSelectedStore}
                              />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                            <div className="input-blocks">
                              <Link className="btn btn-filters ms-auto">
                                <i data-feather="search" className="feather-search" /> Search
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Tableau des stocks faibles */}
                    <div className="table-responsive">
                      <Table columns={columns} dataSource={filteredLowProducts} />
                    </div>
                  </div>
                </div>
              </div>
              {/* Onglet en rupture de stock */}
              <div className="tab-pane fade" id="pills-profile" role="tabpanel" aria-labelledby="pills-profile-tab">
                <div className="card table-list-card">
                  <div className="card-body">
                    <div className="table-top">
                      <div className="search-set">
                        <div className="search-input">
                          <input
                            type="text"
                            placeholder="Search"
                            className="form-control form-control-sm formsearch"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <Link to="#" className="btn btn-searchset">
                            <i data-feather="search" className="feather-search" />
                          </Link>
                        </div>
                      </div>
                      {/* Boutons de téléchargement pour rupture de stock */}
                      <li>
                        <OverlayTrigger placement="top" overlay={renderTooltip}>
                          <Link onClick={() => handleDownloadPDF(filteredOutOfProducts, "Produits en rupture de stock")}>
                            <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                          </Link>
                        </OverlayTrigger>
                      </li>
                      <li>
                        <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                          <Link onClick={() => handleDownloadExcel(filteredOutOfProducts, "Produits en rupture de stock")}>
                            <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                          </Link>
                        </OverlayTrigger>
                      </li>
                      <div className="search-path">
                        <Link className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} id="filter_search">
                          <Filter className="filter-icon" onClick={toggleFilterVisibility} />
                          <span onClick={toggleFilterVisibility}>
                            <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                          </span>
                        </Link>
                      </div>
                    </div>
                    {/* Filtres pour rupture de stock */}
                    <div className="card" id="filter_inputs1">
                      <div className="card-body pb-0">
                        <div className="row">
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Box className="info-img" />
                              <Select
                                className="img-select"
                                options={productlist}
                                classNamePrefix="react-select"
                                placeholder="Choisis Produit"
                                value={selectedProduct}
                                onChange={setSelectedProduct}
                              />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Zap className="info-img" />
                              <Select
                                className="img-select"
                                options={category}
                                classNamePrefix="react-select"
                                placeholder="Choisis Catégorie"
                                value={selectedCategory}
                                onChange={setSelectedCategory}
                              />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Archive className="info-img" />
                              <Select
                                className="img-select"
                                options={store}
                                classNamePrefix="react-select"
                                placeholder="Choisis Warehouse"
                                value={selectedStore}
                                onChange={setSelectedStore}
                              />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                            <div className="input-blocks">
                              <Link className="btn btn-filters ms-auto">
                                <i data-feather="search" className="feather-search" /> Search
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Tableau des produits en rupture de stock */}
                    <div className="table-responsive">
                      <Table columns={columns} dataSource={filteredOutOfProducts} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>      
      {/* Rendu conditionnel du modal d'édition */}
      {isEditModalVisible && (
        <EditProduct
          product={selectedEditProduct}
          onClose={() => setIsEditModalVisible(false)}
        />
      )}
    </div>
  );
};

export default LowStock;
